import RecordVideo from "../../images/configuration/record-video.svg?react";
import RecordAudio from "../../images/configuration/record-audio.svg?react";
import RecordScreen from "../../images/configuration/record-screen.svg?react";
import RecordWebTraffic from "../../images/configuration/record-web-traffic.svg?react";
import detectSidePanel from "../../images/configuration/side-panel.svg?react";
import OnlyOneScreen from "../../images/configuration/one-screen.svg?react";
import DisableNewTab from "../../images/configuration/disable-new-tab.svg?react";
import CloseOpenTabs from "../../images/configuration/close-open-tabs.svg?react";
import DisableClipboard from "../../images/configuration/disable-clipboard.svg?react";
import Whiteboard from "../../images/configuration/whiteboard.svg?react";
import CodeEditor from "../../images/configuration/code-editor.svg?react";
import Calculator from "../../images/configuration/calculator.svg?react";
import DisablePrinting from "../../images/configuration/disable-printing.svg?react";
import DisableRightClick from "../../images/configuration/disable-right-click.svg?react";
import DisableExtensions from "../../images/configuration/disable-extension.svg?react";
import IdVerification from "../../images/configuration/id-verification.svg?react";
import RecordClipboard from "../../images/configuration/record-clipboard.svg?react";
import RecordKeyboard from "../../images/configuration/record-keyboard.svg?react";
import DisableSpellcheck from "../../images/configuration/disable-spellcheck.svg?react";
import DisableFileDownload from "../../images/configuration/disable-download.svg?react";
import BlockApplications from "../../images/configuration/block-applications.svg?react";
import BlockVirtualMachines from "../../images/configuration/block-virtual-machine.svg?react";
import RecordAppsActivity from "../../images/configuration/record-application.svg?react";
import FaceDetection from "../../images/configuration/face-detection.svg?react";
import ObjectDetection from "../../images/configuration/object-detection.svg?react";
import ScanRoom from "../../images/configuration/scan-room.svg?react";
import WebsiteAccess from "../../images/configuration/website-access.svg?react";
import ScratchNotes from "../../images/configuration/work-attachments.svg?react";
import GuidedAccess from "../../images/configuration/guided-access.svg?react";
import RestrictDevices from "../../images/configuration/restrict-mobile.svg?react";
import RestrictHandoff from "../../images/configuration/restrict-handoff.svg?react";
import Signature from "../../images/configuration/signature.svg?react";
import EnableGeofence from "../../images/configuration/geofence.svg?react";
import DisableVirtualCamera from "../../images/configuration/disable-virtual-camera.svg?react";
import TabControl from "../../images/configuration/tab-control.svg?react";
import LogInstalledExtensions from "../../images/configuration/log-installed-extensions.svg?react";
import { ProctoringAssessmentConfigurationV2, ProctoringOption } from "@verity/proctoring-option/types";
import { ReactNode } from "react";
import { Link } from "@mui/material";
import i18nPackage from "../../../i18n/i18n";
import { Trans } from "react-i18next";

export const PROCTORING_OPTION_CATEGORIES = [
	"record",
	"verification",
	"disableFeatures",
	"otherActivities",
	"showTools",
] as const;

export type ProctoringOptionCategory = (typeof PROCTORING_OPTION_CATEGORIES)[number];

export type ConfigurationPageName =
	| "thirdParty"
	| "platform"
	| "profile"
	| "configuration"
	| "method"
	| "liveSessions"
	| "accommodations"
	| "confirmation";

type StandaloneConfigurationPageName = "assessmentDetails" | "inviteTestTakers";

export type VerityAssessmentPageName =
	| "assessmentType"
	| "assessmentDetails"
	| "instructions"
	| "questions"
	| "editQuiz"
	| "review";

export type EditQuizStep =
	| ConfigurationPageName
	| StandaloneConfigurationPageName
	| ProctoringOptionCategory
	| VerityAssessmentPageName;

type DisplayPlatform = "extension" | "lockdown" | "mobile" | "chromeOs";

const restrictDevicesDesALabel = i18nPackage.t(
	"editQuizPage.proctoringOptionsTextTSX.otherActivities.restrictDevices.descriptionAriaLabel",
);

type ProctoringOptionTextConfig = {
	header: string;
	description: ReactNode;
	icon?: typeof RecordVideo;
	category?: ProctoringOptionCategory;
	settingDescription?: ReactNode;
};

export const PROCTORING_OPTION_TEXT: {
	[O in keyof ProctoringOption]-?: ProctoringOptionTextConfig;
} = {
	videoRecordingMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.videoRecordingMode.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.videoRecordingMode.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.videoRecordingMode.setting"),
		icon: RecordVideo,
		category: "record",
	},
	audioRecordingMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.audioRecordingMode.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.audioRecordingMode.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.audioRecordingMode.setting"),
		icon: RecordAudio,
		category: "record",
	},
	recordScreen: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.recordScreen.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.recordScreen.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.recordScreen.setting"),
		icon: RecordScreen,
		category: "record",
	},
	scanRoom: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.scanRoom.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.scanRoom.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.scanRoom.setting"),
		icon: ScanRoom,
		category: "record",
	},
	recordWebTraffic: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordWebTraffic.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordWebTraffic.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordWebTraffic.setting"),
		icon: RecordWebTraffic,
		category: "otherActivities",
	},
	detectSidePanel: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.detectSidePanel.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.detectSidePanel.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.detectSidePanel.setting"),
		icon: detectSidePanel,
		category: "otherActivities",
	},
	recordInput: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordInput.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordInput.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordInput.setting"),
		icon: RecordKeyboard,
		category: "otherActivities",
	},
	recordClipboard: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordClipboard.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordClipboard.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.recordClipboard.setting"),
		icon: RecordClipboard,
		category: "otherActivities",
	},
	excludedWebTrafficRecordingUrls: {
		header: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.excludedWebTrafficRecordingUrls.header",
		),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.excludedWebTrafficRecordingUrls.description",
		),
		icon: RecordWebTraffic,
		category: "otherActivities",
	},

	disableNewTab: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.disableNewTab.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.disableNewTab.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.disableNewTab.setting"),
		icon: DisableNewTab,
		category: "otherActivities",
	},
	closeOpenTabs: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.closeOpenTabs.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.closeOpenTabs.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.closeOpenTabs.setting"),
		icon: CloseOpenTabs,
		category: "otherActivities",
	},
	disableClipboardMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableClipboardMode.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableClipboardMode.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableClipboardMode.setting",
		),
		icon: DisableClipboard,
		category: "disableFeatures",
	},
	enableGuidedAccessMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.enableGuidedAccessMode.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.enableGuidedAccessMode.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.enableGuidedAccessMode.setting",
		),
		icon: GuidedAccess,
		category: "disableFeatures",
	},
	restrictHandoffMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.restrictHandoffMode.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.restrictHandoffMode.description"),
		settingDescription: (
			<Trans
				i18n={i18nPackage}
				i18nKey={"editQuizPage.proctoringOptionsTextTSX.disableFeatures.restrictHandoffMode.setting"}
				components={{
					a: (
						<Link
							href="https://support.yuja.com/hc/en-us/articles/35168712896791-Disabling-Handoff-on-Apple-Devices"
							target="_blank"
							rel="noreferrer"
						/>
					),
				}}
			/>
		),
		icon: RestrictHandoff,
		category: "disableFeatures",
	},
	disableDownloadFiles: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableDownloadFiles.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableDownloadFiles.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableDownloadFiles.setting",
		),
		icon: DisableFileDownload,
		category: "disableFeatures",
	},
	disableSpellcheck: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableSpellcheck.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableSpellcheck.description"),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableSpellcheck.setting",
		),
		icon: DisableSpellcheck,
		category: "disableFeatures",
	},
	disablePrinting: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disablePrinting.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disablePrinting.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disablePrinting.setting"),
		icon: DisablePrinting,
		category: "disableFeatures",
	},
	disableRightClick: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableRightClick.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableRightClick.description"),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableRightClick.setting",
		),
		icon: DisableRightClick,
		category: "disableFeatures",
	},
	extensionBlockMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.extensionBlockMode.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.extensionBlockMode.description"),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.extensionBlockMode.setting",
		),
		icon: DisableExtensions,
		category: "disableFeatures",
	},
	tabWhiteList: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.tabWhiteList.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.tabWhiteList.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.tabWhiteList.setting"),
		icon: WebsiteAccess,
		category: "otherActivities",
	},
	logInstalledExtensions: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.logInstalledExtensions.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.logInstalledExtensions.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.logInstalledExtensions.setting",
		),
		icon: LogInstalledExtensions,
		category: "otherActivities",
	},
	tabBlockList: {
		header: "",
		description: "",
	},

	oneScreenEnforcementMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.oneScreenEnforcementMode.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.oneScreenEnforcementMode.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.oneScreenEnforcementMode.setting",
		),
		icon: OnlyOneScreen,
		category: "otherActivities",
	},
	blockVirtualMachines: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.blockVirtualMachines.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.blockVirtualMachines.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.blockVirtualMachines.setting",
		),
		icon: BlockVirtualMachines,
		category: "disableFeatures",
	},
	applicationBlockMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.applicationBlockMode.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.applicationBlockMode.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.applicationBlockMode.setting",
		),
		icon: BlockApplications,
		category: "disableFeatures",
	},
	recordApplicationsActivity: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.recordApplicationsActivity.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.recordApplicationsActivity.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.recordApplicationsActivity.setting",
		),
		icon: RecordAppsActivity,
		category: "disableFeatures",
	},
	hideOtherWindows: {
		header: "",
		description: "",
	},

	idVerification: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.verification.idVerification.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.verification.idVerification.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.verification.idVerification.setting"),
		icon: IdVerification,
		category: "verification",
	},

	uploadSignature: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.verification.uploadSignature.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.verification.uploadSignature.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.verification.uploadSignature.setting"),
		icon: Signature,
		category: "verification",
	},

	enableCalculator: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableCalculator.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableCalculator.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableCalculator.setting"),
		icon: Calculator,
		category: "showTools",
	},
	enableWhiteboard: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableWhiteboard.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableWhiteboard.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableWhiteboard.setting"),
		icon: Whiteboard,
		category: "showTools",
	},
	enableCodeEditor: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableCodeEditor.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableCodeEditor.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.showTools.enableCodeEditor.setting"),
		icon: CodeEditor,
		category: "showTools",
	},
	codeEditorRoomId: {
		header: "",
		description: "",
	},

	faceTrackingMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.faceTrackingMode.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.faceTrackingMode.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.faceTrackingMode.setting"),
		icon: FaceDetection,
		category: "record",
	},
	objectDetectionMode: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.objectDetectionMode.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.objectDetectionMode.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.objectDetectionMode.setting"),
		icon: ObjectDetection,
		category: "record",
	},
	secondaryVideoRecordingMode: {
		header: "",
		description: "",
	},

	isExtension: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.extension"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.extensionDescription"),
	},
	isLockdown: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.lockdown"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.lockdownDescription"),
	},
	isMobile: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.mobile"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.mobileDescription"),
	},
	isChromeOs: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.chromeOs"),
		description: "",
	},

	keyboardShortcutBlockList: {
		header: "",
		description: "",
	},
	blockWindowedApplications: {
		header: "",
		description: "",
	},
	applicationBlockList: {
		header: "",
		description: "",
	},
	allowedApplicationsList: {
		header: "",
		description: "",
	},
	allowedExtensionsList: {
		header: "",
		description: "",
	},
	showRecordingPanel: {
		header: "",
		description: "",
	},
	enforceLockdown: {
		header: "",
		description: "",
	},
	enforceMobile: {
		header: "",
		description: "",
	},
	idVerificationEnableAi: {
		header: "",
		description: "",
	},
	quietEnvironment: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.audioRecordingMode.quietEnvironmentTitle"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.record.audioRecordingMode.quietEnvironmentDesc"),
	},
	uploadScratchNotes: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.uploadScratchNotes.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.uploadScratchNotes.description"),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.otherActivities.uploadScratchNotes.setting",
		),
		icon: ScratchNotes,
		category: "otherActivities",
	},
	idVerificationMode: {
		header: "",
		description: "",
	},
	restrictDevices: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.restrictDevices.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.restrictDevices.description"),
		settingDescription: (
			<Trans
				i18n={i18nPackage}
				i18nKey={"editQuizPage.proctoringOptionsTextTSX.otherActivities.restrictDevices.setting"}
				components={{
					a: (
						<Link
							href="https://support.yuja.com/hc/en-us/articles/18378524033559-Overview-of-YuJa-Verity-Proctor-Configurations"
							target="_blank"
							rel="noreferrer"
							aria-label={restrictDevicesDesALabel}
						/>
					),
				}}
			/>
		),
		icon: RestrictDevices,
		category: "otherActivities",
	},
	audioTranscription: {
		header: "",
		description: (
			<Trans
				i18nKey={"editQuizPage.proctoringOptionsTextTSX.record.audio.audioTranscription.header"}
				i18n={i18nPackage}
				components={{ b: <strong /> }}
			/>
		),
	},
	flaggedKeywords: {
		header: "",
		description: (
			<Trans
				i18nKey={"editQuizPage.proctoringOptionsTextTSX.record.audio.flaggedKeywords.header"}
				i18n={i18nPackage}
				components={{ b: <strong /> }}
			/>
		),
	},
	enableGeofence: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.enableGeofence.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.enableGeofence.description"),
		settingDescription: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.otherActivities.enableGeofence.setting"),
		icon: EnableGeofence,
		category: "otherActivities",
	},
	geofenceLocations: {
		header: "",
		description: "",
	},
	disableVirtualCamera: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableVirtualCamera.header"),
		description: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableVirtualCamera.description",
		),
		settingDescription: i18nPackage.t(
			"editQuizPage.proctoringOptionsTextTSX.disableFeatures.disableVirtualCamera.setting",
		),
		icon: DisableVirtualCamera,
		category: "disableFeatures",
	},
};

// For UI that groups ProctoringOptionKey and doesn't have a dedicated proctoringOption key
const GROUPED_OPTION_KEYS = ["tabControl"] as const;

export type GroupedOptionKey = (typeof GROUPED_OPTION_KEYS)[number];

export type RenderableProctoringKey = keyof ProctoringOption | GroupedOptionKey;

const TAB_KEYS_LIST = [
	"closeOpenTabs",
	"disableNewTab",
	"recordWebTraffic",
	"excludedWebTrafficRecordingUrls",
	"tabWhiteList",
] as const;

// The UI wants these tab keys to be grouped under "Tab Control". Each of these keys has it's own UI under "Tab Control".
// UI also wants TabControl to have it's own header/description/icon, but there is no database key to store "header/description/icon/etc"
// Also the UI for ConfigurationSummaryList returns a map with ProctoringOption as the key, but don't want to create a database field just for "tabControl"
type TabProctoringKey = (typeof TAB_KEYS_LIST)[number];

export function isTabKey(key: RenderableProctoringKey): key is TabProctoringKey {
	return (TAB_KEYS_LIST as readonly string[]).includes(key);
}

export function isGroupedOptionKey(key: RenderableProctoringKey): key is GroupedOptionKey {
	return (GROUPED_OPTION_KEYS as readonly string[]).includes(key);
}

export const GROUPED_OPTION_TEXT: Record<GroupedOptionKey, ProctoringOptionTextConfig> = {
	tabControl: {
		header: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.groupedOption.tabControl.header"),
		description: i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.groupedOption.tabControl.description"),
		icon: TabControl,
		category: "otherActivities",
	},
};

export function getOptionsInCategory(proctoringOptionCategory: ProctoringOptionCategory) {
	return Object.entries(PROCTORING_OPTION_TEXT)
		.filter((x) => x[1].category === proctoringOptionCategory && x[1].settingDescription)
		.map((x) => x[0] as keyof ProctoringOption);
}

export function getDisplayedPlatformsFromProctoringOptions(
	options: Partial<Pick<ProctoringOption, "isExtension" | "isLockdown" | "isMobile" | "isChromeOs">>,
): DisplayPlatform[] {
	const platforms: DisplayPlatform[] = [];
	if (options.isExtension) {
		platforms.push("extension");
	}
	if (options.isLockdown) {
		platforms.push("lockdown");
	}
	if (options.isMobile) {
		platforms.push("mobile");
	}
	if (options.isLockdown && options.isChromeOs) {
		platforms.push("chromeOs");
	}
	return platforms;
}

export function getProctoringPlatformName(platform: DisplayPlatform): string {
	return i18nPackage.t(`editQuizPage.proctoringOptionsTextTSX.platform.${platform}`);
}

export function getPlatformTextFromProctoringOptions(
	options: Partial<Pick<ProctoringOption, "isExtension" | "isLockdown" | "isMobile" | "isChromeOs">>,
) {
	const platformText = getDisplayedPlatformsFromProctoringOptions(options).map(getProctoringPlatformName);
	if (platformText.length === 0) {
		return i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.none");
	} else if (platformText.length === 1) {
		return platformText[0];
	} else if (platformText.length === 2) {
		return i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.and2", {
			platform1: platformText[0],
			platform2: platformText[1],
		});
	} else {
		return i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.platform.andMany", {
			platforms: platformText.slice(0, -1).join(", "),
			lastPlatform: platformText[platformText.length - 1],
		});
	}
}

export function getProctoringMethodName(
	proctoringAssessmentConfiguration: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">,
) {
	if (proctoringAssessmentConfiguration.isLiveProctored) {
		return i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.method.isLiveProctored");
	} else if (proctoringAssessmentConfiguration.isLivePopIn) {
		if (proctoringAssessmentConfiguration.enablePostReview) {
			return i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.method.enablePostReview");
		} else {
			return i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.method.isLivePopIn");
		}
	}
	return i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.method.automated");
}

export function isProctoringOptionCategory(pageName: string): pageName is ProctoringOptionCategory {
	return PROCTORING_OPTION_CATEGORIES.includes(pageName as ProctoringOptionCategory);
}

export function getEditQuizStepName(pageName: EditQuizStep): string {
	return i18nPackage.t(`editQuizPage.proctoringOptionsTextTSX.stepName.${pageName}`);
}
